import { AmountInputField, SelectField, CheckboxField } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { formatMoney } from '@hex/calculators-shared/utils';
import { Icon, SelectItem, Flex, tokens, Label, Button, HelpButton, Typography } from '@sapphire-react/core';
import { Wallet, Group } from '@sapphire/icons/react';
import React, { Dispatch, RefObject, useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MAXIMUM_INCOME_AMOUNT } from '../../../constants';
import { useAffordabilityForm } from '../../hooks/useAffordabilityForm';
import { AffordabilityFormDataType, maritalStatuses, AffordabilityResponse } from '../../types';
import { CoApplicant } from './components/CoApplicant';
import { FollowingQuestionsTooltip } from './components/FollowingQuestionsTooltip';
import { HouseholdSection } from './components/HouseholdSection';

type Props = {
  buttonRef: RefObject<any>;
  affordabilityResponseData: AffordabilityResponse | null;
  loading?: boolean;
  isDisabled?: boolean;
  setIsDisabled?: Dispatch<React.SetStateAction<boolean>>;
  onFormChange: () => void;
  onSubmit: (data: AffordabilityFormDataType, reset: UseFormReset<AffordabilityFormDataType>) => void;
};

export const AffordabilityInputs = ({
  onSubmit,
  affordabilityResponseData,
  onFormChange,
  buttonRef,
  loading,
}: Props) => {
  const { t } = useTranslation('affordability');
  const { isExtraSmall, isSmall } = useSapphireMedia();

  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const { control, handleSubmit, reset, watch, isDirty, trackCalculatorStarted } = useAffordabilityForm();

  useEffect(() => {
    if (isDirty && affordabilityResponseData) {
      onFormChange();
    }
  }, [isDirty, onFormChange, affordabilityResponseData]);

  const getMaritalStatusTranslationKey = (value: string) => {
    switch (value) {
      case maritalStatuses.cohabitant:
        return 'form.maritalStatus.options.cohabitant';
      case maritalStatuses.married:
        return 'form.maritalStatus.options.married';
      case maritalStatuses.single:
        return 'form.maritalStatus.options.single';
      case maritalStatuses.widow:
        return 'form.maritalStatus.options.widow';
      default:
        return '';
    }
  };

  return (
    <S.Form
      onSubmit={handleSubmit((formData) => onSubmit(formData, reset))}
      autoComplete={'off'}
      onClick={trackCalculatorStarted}
    >
      <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
        <CoApplicant control={control} loading={loading} />
        <AmountInputField
          control={control}
          name={'primaryApplicantIncome'}
          label={t('form.primaryIncome.label')}
          width={'100%'}
          postfix={'€'}
          min={0}
          max={MAXIMUM_INCOME_AMOUNT}
          rules={{
            required: t('form.primaryIncome.error'),
          }}
          prefix={
            <Icon size={inputSize}>
              <Wallet />
            </Icon>
          }
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={t('help')}
              contentBody={
                <Typography.Body size={'sm'} aria-live={'polite'}>
                  {t('form.primaryIncome.tooltip')}
                </Typography.Body>
              }
            />
          }
        />
        {watch('secondaryApplicantExist') === '1' && (
          <AmountInputField
            control={control}
            name={'secondaryApplicantIncome'}
            label={t('form.secondaryIncome.label')}
            width={'100%'}
            postfix={'€'}
            min={0}
            max={MAXIMUM_INCOME_AMOUNT}
            rules={{
              required: t('form.secondaryIncome.error'),
            }}
            prefix={
              <Icon size={inputSize}>
                <Wallet />
              </Icon>
            }
          />
        )}

        <SelectField
          control={control}
          name={'maritalStatus'}
          data-test-id={'maritalStatus'}
          label={t('form.maritalStatus.label')}
          width={'100%'}
          rules={{
            required: t('form.maritalStatus.error'),
          }}
          prefix={
            <Icon
              size={inputSize}
              UNSAFE_style={{ marginLeft: 3, marginTop: 2, color: tokens.color.foregroundSecondary.value }}
            >
              <Group />
            </Icon>
          }
        >
          {[...Object.values(maritalStatuses)].map((value) => (
            <SelectItem key={`${value}`}>{t(getMaritalStatusTranslationKey(value))}</SelectItem>
          ))}
        </SelectField>
        <HouseholdSection control={control} />
        <Label
          htmlFor={'followingQuestion'}
          style={{ display: 'inline-block' }}
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={t('help')}
              contentBody={<FollowingQuestionsTooltip tKey={'form.followingQuestion.tooltip'} />}
            />
          }
        >
          <Typography.Body size={'sm'} fontWeight={tokens.font.weightDefaultMedium}>
            {t('form.followingQuestion.label')}
          </Typography.Body>
        </Label>

        <CheckboxField name={'propertiesToSell'} control={control}>
          {t('form.propertiesToSell.label')}
        </CheckboxField>
        {watch('propertiesToSell') && (
          <S.OtherInformationField>
            <AmountInputField
              control={control}
              name={'loansRemainingBalance'}
              label={t('form.loansRemainingBalance.label')}
              width={'100%'}
              postfix={'€'}
              min={0}
              max={100_000_000}
              autoComplete={'off'}
              rules={{
                required: t('form.loansRemainingBalance.error'),
              }}
              contextualHelp={
                <HelpButton
                  variant={'help'}
                  aria-label={t('help')}
                  contentBody={
                    <Typography.Body size={'sm'} aria-live={'polite'}>
                      {t('form.loansRemainingBalance.tooltip')}
                    </Typography.Body>
                  }
                />
              }
            />
            <br />
            <AmountInputField
              control={control}
              name={'housingCompanyDebt'}
              label={t('form.housingCompanyDebt.label')}
              width={'100%'}
              postfix={'€'}
              min={0}
              max={100_000_000}
              rules={{
                required: t('form.housingCompanyDebt.error'),
              }}
              contextualHelp={
                <HelpButton
                  variant={'help'}
                  aria-label={t('help')}
                  contentBody={
                    <Typography.Body size={'sm'} aria-live={'polite'}>
                      {t('form.housingCompanyDebt.tooltip')}
                    </Typography.Body>
                  }
                />
              }
            />
          </S.OtherInformationField>
        )}

        <CheckboxField name={'otherLoans'} control={control}>
          {t('form.otherLoans.label')}
        </CheckboxField>
        {watch('otherLoans') && (
          <S.OtherInformationField>
            <AmountInputField
              control={control}
              name={'personalLoansBalance'}
              label={t('form.personalLoansBalance.label')}
              width={'100%'}
              postfix={'€'}
              min={0}
              max={100_000_000}
              rules={{
                required: t('form.personalLoansBalance.error'),
              }}
              contextualHelp={
                <HelpButton
                  variant={'help'}
                  aria-label={t('help')}
                  contentBody={
                    <Typography.Body size={'sm'} aria-live={'polite'}>
                      {t('form.personalLoansBalance.tooltip')}
                    </Typography.Body>
                  }
                />
              }
            />
          </S.OtherInformationField>
        )}

        <CheckboxField name={'creditCards'} control={control}>
          {t('form.creditCards.label')}
        </CheckboxField>
        {watch('creditCards') && (
          <S.OtherInformationField>
            <AmountInputField
              control={control}
              name={'creditCardsLimit'}
              label={t('form.creditCardsLimit.label')}
              width={'100%'}
              postfix={'€'}
              min={0}
              max={100_000_000}
              rules={{
                required: t('form.creditCardsLimit.error'),
              }}
              contextualHelp={
                <HelpButton
                  variant={'help'}
                  aria-label={t('help')}
                  contentBody={
                    <Typography.Body size={'sm'} aria-live={'polite'}>
                      {t('form.creditCardsLimit.tooltip', {
                        creditLimit: formatMoney(1000, { currency: '€' }),
                        usedAmount: formatMoney(500, { currency: '€' }),
                      })}
                    </Typography.Body>
                  }
                />
              }
            />
          </S.OtherInformationField>
        )}

        <Flex justifyContent={'center'} marginTop={tokens.size.spacingMd}>
          <Button
            ref={buttonRef}
            type={'submit'}
            isDisabled={loading}
            name={'submit'}
            data-test-id={'form-submit'}
            minHeight={44}
          >
            {loading ? t('loading') : t('form.submit')}
          </Button>
        </Flex>
      </Flex>
    </S.Form>
  );
};

const S = {
  Form: styled.form`
    display: flex;
    flex-direction: column;
  `,
  OtherInformationField: styled.div`
    padding-left: 30px;
  `,
};
